import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

export type TooltipPosition = `top` | `bottom` | `left` | `right`;

interface TooltipProps {
    /**
     * The content to display in the tooltip (string or React node)
     */
    content: React.ReactNode;

    /**
     * The trigger element that shows the tooltip on hover/focus
     */
    children: React.ReactElement<React.AriaAttributes>;

    /**
     * Preferred position of the tooltip relative to the trigger element
     * @default 'top'
     */
    position?: TooltipPosition;

    /**
     * Delay in milliseconds before showing the tooltip
     * @default 200
     */
    delay?: number;

    /**
     * Whether the tooltip is disabled (won't show)
     * Note: Tooltip is also implicitly disabled if content is empty
     * @default false
     */
    disabled?: boolean;

    /**
     * Optional max width constraint in pixels
     * @default 200
     */
    maxWidth?: number;
}

/**
 * Tooltip component for displaying contextual help text.
 * Styled with light background (neutral-50) and dark text (neutral-950).
 *
 * Features:
 * - Multiple positioning options with automatic overflow handling
 * - Triggers on hover and focus
 * - Smooth fade animations
 * - Portal rendering to avoid z-index issues
 * - Accessible with ARIA labels and keyboard navigation
 * - Automatically disabled when content is empty/null
 *
 * @example
 * ```tsx
 * <Tooltip content="This is a helpful message" position="top">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 */

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    position = `top`,
    delay = 200,
    disabled = false,
    maxWidth = 200
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check if content is empty (null, undefined, empty string, or whitespace-only string)
    const isContentEmpty = !content || (typeof content === `string` && content.trim() === ``);
    const isDisabled = disabled || isContentEmpty;

    // Configure Popper
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: position,
        modifiers: [
            {
                name: `offset`,
                options: {
                    offset: [0, 8] // 8px gap between trigger and tooltip
                }
            },
            {
                name: `preventOverflow`,
                options: {
                    padding: 8
                }
            },
            {
                name: `flip`,
                options: {
                    fallbackPlacements: [`top`, `bottom`, `left`, `right`]
                }
            }
        ]
    });

    const handleShow = () => {
        if (isDisabled) return;

        // Clear any pending hide
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
        }

        showTimeoutRef.current = setTimeout(() => {
            setShouldRender(true);
            // Small delay to allow DOM to render before animating
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsVisible(true);
                });
            });
        }, delay);
    };

    // Only show tooltip on keyboard focus — skip programmatic focus (e.g. modal focus trap)
    const handleFocus = (e: React.FocusEvent) => {
        if (!e.relatedTarget) return;
        handleShow();
    };

    // Hide tooltip with exit animation
    const handleHide = () => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
        }
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }

        setIsVisible(false);

        // Wait for exit animation to complete before unmounting
        hideTimeoutRef.current = setTimeout(() => {
            setShouldRender(false);
        }, 200); // Match the transition duration
    };

    // Cleanup timeouts on unmount
    useEffect(() => () => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
        }
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
    }, []);

    // Wrap children in a span that captures events (works for disabled elements too)
    const triggerElement = (
        <span
            ref={setReferenceElement}
            onMouseEnter={handleShow}
            onMouseLeave={handleHide}
            onFocus={handleFocus}
            onBlur={handleHide}
            style={{ display: `inline-block` }}
        >
            {React.cloneElement(children, {
                'aria-describedby': isVisible ? `tooltip` : undefined
            })}
        </span>
    );

    // Get the actual placement from Popper (it may differ from preferred position due to flipping)
    const actualPlacement = attributes.popper?.[`data-popper-placement`] as string | undefined;
    const getTransformOrigin = () => {
        if (!actualPlacement) return `center`;

        if (actualPlacement.startsWith(`top`)) return `center bottom`;
        if (actualPlacement.startsWith(`bottom`)) return `center top`;
        if (actualPlacement.startsWith(`left`)) return `right center`;
        if (actualPlacement.startsWith(`right`)) return `left center`;

        return `center`;
    };

    return (
        <>
            {triggerElement}
            {shouldRender && !isDisabled && createPortal(
                <div
                    ref={setPopperElement}
                    style={{
                        ...styles.popper,
                        zIndex: 9999
                    }}
                    {...attributes.popper}
                    role="tooltip"
                    id="tooltip"
                >
                    <div
                        className={classNames(
                            `bg-neutral-50 text-neutral-800 text-[12px] leading-[12px] font-normal px-[8px] py-[10px] rounded-[8px]`,
                            `tracking-[0.12px]`
                        )}
                        style={{
                            maxWidth: `${maxWidth}px`,
                            boxShadow: `0px 2px 4px 0px rgba(34, 41, 51, 0.24)`,
                            opacity: isVisible ? 1 : 0,
                            transform: isVisible ? `scale(1, 1)` : `scale(0.75, 0.5625)`,
                            transition: isVisible
                                ? `opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 133ms cubic-bezier(0.4, 0, 0.2, 1)`
                                : `opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 67ms`,
                            transformOrigin: getTransformOrigin()
                        }}
                    >
                        {content}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
